import { agentMayReviseSource } from "@repo/shared/findingOwnership";
import { findingStalenessChip, findingStalenessLabel } from "@repo/shared/findingStaleness";
import { normalizeFindingStatus } from "@repo/shared/findingStatus";
/**
 * How a finding is presented, in one place: its status pill, its severity
 * word, and where it came from. The policy tab and the finding's own page
 * both read from here to keep decision labels consistent. The organization queue uses the same
 * vocabulary as the document tab and detail page.
 *
 * @module components/regulation/finding/findingView
 */
import type { PolicyFinding, PolicyFindingStatus } from "../../../state/governance";

export const FINDING_STATUS_PILL: Record<
  PolicyFindingStatus,
  { label: string; className: string }
> = {
  open: { label: "Open", className: "bg-destructive/10 text-destructive" },
  resolved: {
    label: "Resolved",
    className: "bg-emerald-600/10 text-emerald-600 dark:text-emerald-300/90",
  },
  closed: { label: "Closed", className: "bg-muted text-muted-foreground" },
};

export const FINDING_SEVERITY_LABELS: Record<string, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

export interface FindingSeverityTone {
  readonly rail: string;
  readonly surface: string;
  readonly badge: string;
}

const FINDING_SEVERITY_TONES: Record<string, FindingSeverityTone> = {
  critical: { rail: "border-s-red-600", surface: "bg-gradient-to-r from-red-500/[0.09] via-red-500/[0.025] to-transparent", badge: "bg-red-600/12 text-red-700 dark:text-red-300" },
  high: { rail: "border-s-orange-500", surface: "bg-gradient-to-r from-orange-500/[0.09] via-orange-500/[0.025] to-transparent", badge: "bg-orange-500/12 text-orange-700 dark:text-orange-300" },
  medium: { rail: "border-s-amber-400", surface: "bg-gradient-to-r from-amber-400/[0.09] via-amber-400/[0.025] to-transparent", badge: "bg-amber-400/15 text-amber-800 dark:text-amber-200" },
  low: { rail: "border-s-sky-500", surface: "bg-gradient-to-r from-sky-500/[0.08] via-sky-500/[0.02] to-transparent", badge: "bg-sky-500/12 text-sky-700 dark:text-sky-300" },
};

const NEUTRAL_SEVERITY_TONE: FindingSeverityTone = {
  rail: "border-s-border",
  surface: "bg-card",
  badge: "bg-muted text-muted-foreground",
};

/** A stable visual cue for priority, shared by finding queues and detail. */
export const findingSeverityTone = (severity: string | null): FindingSeverityTone =>
  (severity === null ? undefined : FINDING_SEVERITY_TONES[severity]) ?? NEUTRAL_SEVERITY_TONE;

const FINDING_SEVERITY_INK: Record<string, string> = {
  critical: "text-red-600 dark:text-red-400",
  high: "text-orange-600 dark:text-orange-400",
  medium: "text-amber-600 dark:text-amber-300",
  low: "text-sky-600 dark:text-sky-400",
};

/** The priority's colour as text (Home's "High · …" line), muted when unrated. */
export const findingSeverityInk = (severity: string | null): string =>
  (severity === null ? undefined : FINDING_SEVERITY_INK[severity]) ?? "text-muted-foreground";

/** Who put this finding here, in the words the workspace uses elsewhere. */
export const findingSourceLabel = (source: PolicyFinding["source"]): string =>
  source === "monitor"
    ? "Regulatory monitoring"
    : source === "agent"
      ? "Inspector"
      : "Added by hand";

/** "3 changes" for the card's red count badge; null when the finding asks for none. */
export const findingChangeCountLabel = (count: number): string | null =>
  count <= 0 ? null : `${count} ${count === 1 ? "change" : "changes"}`;

/** Still needs a decision, as opposed to decided. */
export const isFindingOpen = (finding: Pick<PolicyFinding, "status">): boolean =>
  finding.status === "open";

type FindingStalenessInput = Pick<
  PolicyFinding,
  "status" | "assessedRevision" | "policyRevision" | "assessedVersion" | "currentVersion"
>;

/**
 * The sentence form of the same warning, for a page with room for it. Null for
 * a decided finding, like the pill below: a resolved or closed finding is a
 * record of what was decided about the document as it stood, and the document
 * moving on afterwards — the resolving version above all — is not a reason to
 * look again.
 */
export const findingStalenessReason = (finding: FindingStalenessInput): string | null =>
  isFindingOpen(finding) ? findingStalenessLabel(finding) : null;

/**
 * The warning that a finding's assessment was written against a document that
 * has since moved on, as a pill beside the status. Null when there is nothing
 * to warn about, so a caller renders this or nothing without repeating the test
 * — which lives in `@repo/shared/findingStaleness`, not here. Only an open
 * finding warns: resolving one against a new version made every decided
 * finding read "Document changed since".
 *
 * Amber rather than red: the finding is not wrong and nothing has failed. It is
 * describing an older document, which is a reason to look again, not an alarm.
 */
export const findingStalenessPill = (
  finding: FindingStalenessInput,
): { label: string; className: string } | null => {
  const label = isFindingOpen(finding) ? findingStalenessChip(finding) : null;
  return label === null
    ? null
    : { label, className: "bg-amber-400/15 text-amber-800 dark:text-amber-200" };
};

/**
 * Whether Inspector can be asked to re-read this finding against the current
 * document: the same ownership rule the domain API enforces
 * (`agentMayReviseSource`, shared so the button and the call cannot disagree),
 * and only while the finding is still undecided. A finding someone added by
 * hand stays theirs — the warning is still shown, and a person re-grounds it.
 */
export const findingCanBeRechecked = (
  finding: Pick<PolicyFinding, "source" | "status">,
): boolean => agentMayReviseSource(finding.source) && isFindingOpen(finding);

/** Inspection summaries may arrive from an older server; unknown statuses are never a rejection. */
export function findingStatusPill(status: string): { label: string; className: string } {
  const canonical = normalizeFindingStatus(status);
  return canonical
    ? FINDING_STATUS_PILL[canonical]
    : { label: "Unknown", className: "bg-muted text-muted-foreground" };
}
